import React from 'react'
import { Svg, Path, Line, Text, View, G } from '@react-pdf/renderer'
import type { ReportSalesProgressionPoint } from '@/hooks/useProjectReportData'

const NAVY = '#001A36'
const SOLD = '#16a34a'
const AXIS = '#374151'
const GRID = '#e5e7eb'
const MUTED = '#6b7280'
const DASH = '3 2'

const VIEW_W = 500
const VIEW_H = 280
const PAD_LEFT = 38
const PAD_RIGHT = 38
const PAD_TOP = 12
const PAD_BOTTOM = 38

// react-pdf's SVGPresentationAttributes typings don't include fontSize even
// though the renderer reads it at runtime. Cast through `unknown` to satisfy
// the type-checker without sprinkling `@ts-expect-error` everywhere.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const svgFontStyle = (size: number): any => ({ fontSize: size })

interface Props {
  data: ReportSalesProgressionPoint[]
  totalUnits: number | null
}

/**
 * Hand-drawn line chart for project sales follow-up.
 *
 * Uses `@react-pdf/renderer` SVG primitives — NOT the DOM SVG namespace.
 * We index-space the X axis (no date math) and pick at most 8 month labels.
 *
 * Series:
 *  - Cumulative Reservas (solid navy)
 *  - Cumulative Reservas + Sold (solid green)
 *  - Total units reference (dashed, only when totalUnits > 0)
 *
 * Right axis is a dashed 0–100% rescaling of the left axis (anchored to
 * total_units when known) so the reader gets both an absolute count and a
 * sell-through reading from the same chart.
 */
export function ProjectSalesChart({ data, totalUnits }: Props): JSX.Element {
  if (!data || data.length === 0) {
    return (
      <View style={{ alignItems: 'center', paddingVertical: 36 }}>
        <Svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} style={{ width: '100%', height: 140 }}>
          <Text
            x={VIEW_W / 2}
            y={VIEW_H / 2}
            fill={MUTED}
            textAnchor="middle"
            style={svgFontStyle(12)}
          >
            No sales activity recorded yet
          </Text>
        </Svg>
      </View>
    )
  }

  const innerW = VIEW_W - PAD_LEFT - PAD_RIGHT
  const innerH = VIEW_H - PAD_TOP - PAD_BOTTOM

  // Committed = distinct units reserved OR sold by month X. Backend
  // pre-aggregates this using LEAST(reserved_date, sales_date); summing
  // cumulative_reserved + cumulative_sold here would double-count units that
  // went reserved → sold and push the line above total_units.
  const combined = data.map((p) => Number(p.cumulative_committed ?? 0))

  const maxCombined = combined.reduce((m, v) => Math.max(m, v), 0)
  const hasTotal = totalUnits !== null && totalUnits > 0
  // Anchor the y-axis to total units when known so the right-axis 100% mark
  // lines up with the total. Fall back to a 10% headroom otherwise.
  const rawMax = hasTotal
    ? Math.max(totalUnits as number, maxCombined)
    : maxCombined
  const yMax = rawMax > 0 ? (hasTotal ? rawMax : rawMax * 1.1) : 1

  // Index-spaced x positions. If there's only 1 datum, center it.
  const xFor = (i: number) => {
    if (data.length === 1) return PAD_LEFT + innerW / 2
    return PAD_LEFT + (i / (data.length - 1)) * innerW
  }
  const yFor = (v: number) => PAD_TOP + innerH - (v / yMax) * innerH

  const buildPathFromValues = (values: number[]): string => {
    const pts = values.map((v, i) => `${xFor(i)},${yFor(v)}`)
    if (pts.length === 0) return ''
    return 'M ' + pts.join(' L ')
  }

  const pathReserved = buildPathFromValues(
    data.map((p) => Number(p.cumulative_reserved ?? 0)),
  )
  const pathCombined = buildPathFromValues(combined)

  // 5 horizontal tick anchors — used for both left (absolute) and right (%).
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((frac) => ({
    frac,
    value: yMax * frac,
    y: yFor(yMax * frac),
  }))

  // X-axis: at most 8 labels, evenly distributed across data points.
  const maxLabels = Math.min(8, data.length)
  const step = Math.max(1, Math.ceil(data.length / maxLabels))
  const xTicks: { i: number; label: string }[] = []
  for (let i = 0; i < data.length; i += step) {
    xTicks.push({ i, label: formatMonth(data[i].month) })
  }
  // Always include the last point so the user sees the latest month.
  if (xTicks.length === 0 || xTicks[xTicks.length - 1].i !== data.length - 1) {
    xTicks.push({
      i: data.length - 1,
      label: formatMonth(data[data.length - 1].month),
    })
  }

  // Right-axis spine x.
  const rightX = VIEW_W - PAD_RIGHT

  // Dashed reference line at total_units. Only drawn when totalUnits is known
  // AND falls within the visible y range (which it should, by construction).
  const totalY = hasTotal ? yFor(totalUnits as number) : null

  return (
    <View>
      <Svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} style={{ width: '100%', height: 200 }}>
        {/* Horizontal gridlines + LEFT y-axis labels (absolute counts) */}
        <G>
          {ticks.map((t, idx) => (
            <G key={`yt-${idx}`}>
              <Line
                x1={PAD_LEFT}
                y1={t.y}
                x2={rightX}
                y2={t.y}
                strokeWidth={0.5}
                stroke={GRID}
              />
              <Text
                x={PAD_LEFT - 4}
                y={t.y + 3}
                fill={AXIS}
                textAnchor="end"
                style={svgFontStyle(7)}
              >
                {Math.round(t.value).toString()}
              </Text>
            </G>
          ))}
        </G>

        {/* RIGHT y-axis spine (dashed) + percentage labels */}
        <Line
          x1={rightX}
          y1={PAD_TOP}
          x2={rightX}
          y2={PAD_TOP + innerH}
          strokeWidth={0.75}
          stroke={AXIS}
          strokeDasharray={DASH}
        />
        <G>
          {ticks.map((t, idx) => (
            <Text
              key={`yr-${idx}`}
              x={rightX + 4}
              y={t.y + 3}
              fill={AXIS}
              textAnchor="start"
              style={svgFontStyle(7)}
            >
              {`${Math.round(t.frac * 100)}%`}
            </Text>
          ))}
        </G>

        {/* X axis baseline */}
        <Line
          x1={PAD_LEFT}
          y1={PAD_TOP + innerH}
          x2={rightX}
          y2={PAD_TOP + innerH}
          strokeWidth={0.75}
          stroke={AXIS}
        />

        {/* X labels */}
        <G>
          {xTicks.map((t, idx) => (
            <Text
              key={`xt-${idx}`}
              x={xFor(t.i)}
              y={PAD_TOP + innerH + 12}
              fill={AXIS}
              textAnchor="middle"
              style={svgFontStyle(7)}
            >
              {t.label}
            </Text>
          ))}
        </G>

        {/* Total units reference (dashed horizontal at 100% mark) */}
        {totalY !== null && (
          <G>
            <Line
              x1={PAD_LEFT}
              y1={totalY}
              x2={rightX}
              y2={totalY}
              stroke={MUTED}
              strokeWidth={0.75}
              strokeDasharray={DASH}
            />
            <Text
              x={PAD_LEFT + 4}
              y={totalY - 3}
              fill={MUTED}
              textAnchor="start"
              style={svgFontStyle(7)}
            >
              {`Total: ${totalUnits} units (100%)`}
            </Text>
          </G>
        )}

        {/* Data series */}
        <Path d={pathReserved} stroke={NAVY} strokeWidth={1.4} fill="none" />
        <Path d={pathCombined} stroke={SOLD} strokeWidth={1.4} fill="none" />
      </Svg>

      {/* Legend */}
      <View style={{ flexDirection: 'row', justifyContent: 'center', marginTop: 6 }}>
        <LegendDot color={NAVY} label="Reservas (cumulative)" />
        <LegendDot color={SOLD} label="Reservas + Sold (cumulative)" />
        {hasTotal && <LegendDot color={MUTED} label="Total units (100%)" dashed />}
      </View>
    </View>
  )
}

function LegendDot({
  color,
  label,
  dashed,
}: {
  color: string
  label: string
  dashed?: boolean
}): JSX.Element {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', marginRight: 12 }}>
      <Svg viewBox="0 0 16 8" style={{ width: 16, height: 8, marginRight: 4 }}>
        <Line
          x1={0}
          y1={4}
          x2={16}
          y2={4}
          stroke={color}
          strokeWidth={1.6}
          strokeDasharray={dashed ? '3 2' : undefined}
        />
      </Svg>
      <Text style={{ fontSize: 8, color: '#374151' }}>{label}</Text>
    </View>
  )
}

function formatMonth(monthIso: string): string {
  // Backend returns a date string like "2026-05-01". Render as "May '26".
  if (!monthIso) return '—'
  const d = new Date(monthIso)
  if (Number.isNaN(d.getTime())) return monthIso.slice(0, 7)
  const month = d.toLocaleString('en-US', { month: 'short' })
  const yy = String(d.getUTCFullYear()).slice(-2)
  return `${month} '${yy}`
}
